'use client';

import { ChangeEvent, ComponentProps, forwardRef } from 'react';
import { CountryIso2, defaultCountries, usePhoneInput } from 'react-international-phone';
import { cn } from '@/shared/lib/cn';
import { mergeRefs } from '@/shared/ui/form/utils/mergeRefs';
import { getDefaultCountry, SUPPORTED_COUNTRIES } from '@/shared/ui/form/utils/supportedCountries';
import { Select } from '@/shared/ui/select';
import { TextField } from './text-field';

const COUNTRY_CODES = {} as Record<
    CountryIso2,
    {
        name: string;
        dialCode: string;
        format?:
            | string
            | (Record<string, string> & {
                  default: string;
              });
        priority?: number;
        areaCodes?: string[];
    }
>;

// Build key binding to country code to prevent array searching
defaultCountries.forEach(([name, iso2, dialCode, format, priority, areaCodes]) => {
    COUNTRY_CODES[iso2] = {
        name,
        dialCode,
        format,
        priority,
        areaCodes,
    };
});

type Props = Omit<ComponentProps<typeof TextField>, 'onChange'> & {
    onChange?(value: string | undefined): void;
    supportedCountries?: CountryIso2[];
    defaultCountry?: CountryIso2;
    countrySelectTestId?: string | null;
    countryDropdownTestId?: string | null;
    countryOptionTestId?: string | null;
};

export const TelephoneField = forwardRef<HTMLInputElement, Props>(
    (
        {
            state,
            value,
            onChange,
            supportedCountries = SUPPORTED_COUNTRIES,
            defaultCountry = getDefaultCountry(),
            labelProps,
            legendProps,
            fieldsetProps,
            countrySelectTestId,
            countryDropdownTestId,
            countryOptionTestId,
            ...props
        }: Props,
        ref,
    ) => {
        const { country, setCountry, inputRef, inputValue, handlePhoneValueChange } = usePhoneInput({
            defaultCountry,
            disableDialCodeAndPrefix: true,
            value: value as string,
            onChange: (data) => {
                onChange?.(data.phone.replace(`+${data.country.dialCode}`, `+${data.country.dialCode} `));
            },
        });

        return (
            <div className="telephone-field">
                <Select value={country.iso2} onValueChange={(val) => setCountry(val as CountryIso2)}>
                    <Select.Trigger
                        state={state}
                        className="telephone-field__country-select"
                        data-testid={countrySelectTestId ?? undefined}
                        disabled={props.disabled}
                    >
                        +{country.dialCode}
                    </Select.Trigger>
                    <Select.Content
                        className="telephone-field__country-dropdown"
                        data-testid={countryDropdownTestId ?? undefined}
                    >
                        {supportedCountries.map((c: CountryIso2) => (
                            <Select.Item key={c} value={c} data-testid={countryOptionTestId ?? undefined}>
                                +{COUNTRY_CODES[c].dialCode}
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select>
                <TextField
                    ref={mergeRefs([ref, inputRef])}
                    state={state}
                    type="tel"
                    value={inputValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handlePhoneValueChange(e);
                    }}
                    labelProps={labelProps}
                    legendProps={legendProps}
                    fieldsetProps={fieldsetProps}
                    className={cn('telephone-field__input', props.className)}
                    {...props}
                />
            </div>
        );
    },
);
TelephoneField.displayName = 'TelephoneField';
